import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { allProducts } from '@/mocks/productsData';
import ProductGallery from './components/ProductGallery';
import ProductFeatures from './components/ProductFeatures';
import ProductSpecs from './components/ProductSpecs';
import RelatedProducts from './components/RelatedProducts';

function SectionReveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`scroll-reveal ${isVisible ? 'scroll-reveal-visible' : ''} ${delay > 0 ? `scroll-reveal-delay-${Math.min(delay, 6)}` : ''} ${className}`}
    >
      {children}
    </div>
  );
}

export default function ProductDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const productId = Number(id);
  const product = allProducts.find((p) => p.id === productId);

  useEffect(() => {
    if (!product) {
      navigate('/products');
    }
  }, [product, navigate]);

  if (!product) return null;

  const isNew = product.price >= 45000 && product.price <= 56000;
  const isHighPrice = product.price >= 45000;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-14 md:pt-20">
        {/* Hero 區 */}
        <div className="relative overflow-hidden text-white pt-4 md:pt-6 pb-8 md:pb-12">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1B5E40] via-[#256F4A] to-[#1B3D2A]" />
          <div className="absolute -top-20 -right-20 w-72 h-72 md:w-96 md:h-96 rounded-full bg-[#D4A574]/15 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 md:w-64 md:h-64 rounded-full bg-[#2E8B57]/20 blur-2xl" />

          <div className="relative px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
            {/* 麵包屑 */}
            <nav className="flex items-center gap-2 text-sm text-white/70 mb-3">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="hover:text-white transition-colors"
              >
                首頁
              </button>
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </div>
              <button
                type="button"
                onClick={() => navigate('/products')}
                className="hover:text-white transition-colors"
              >
                產品系列
              </button>
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </div>
              <span className="text-white font-medium">{product.name}</span>
            </nav>

            <h1 className="text-2xl md:text-3xl font-bold">{product.name}</h1>
          </div>

          <svg className="absolute bottom-0 left-0 w-full h-auto block" viewBox="0 0 1440 120" fill="none" preserveAspectRatio="none">
            <path d="M0 120L60 108C120 96 240 72 360 60C480 48 600 48 720 54C840 60 960 72 1080 78C1200 84 1320 84 1380 84L1440 84V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>

        {/* 主產品區 */}
        <div className="px-6 md:px-10 lg:px-16 py-8 md:py-14 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-10 lg:gap-16">
            {/* 左側 - 圖片 */}
            <div className="lg:w-1/2">
              <SectionReveal>
                <ProductGallery
                  productName={product.name}
                  image={product.image}
                  productId={product.id}
                />
              </SectionReveal>
            </div>

            {/* 右側 - 資訊 */}
            <div className="lg:w-1/2 flex flex-col">
              <SectionReveal delay={1}>
                {/* 系列名稱 */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-[#D4A574] uppercase tracking-wider">
                    {product.subtitle}
                  </span>
                  {isNew && (
                    <span className="px-2.5 py-0.5 rounded-full bg-[#D4A574] text-white text-xs font-bold">
                      新品
                    </span>
                  )}
                  {isHighPrice && !isNew && (
                    <span className="px-2.5 py-0.5 rounded-full bg-[#1B3D2A] text-white text-xs font-bold">
                      熱銷
                    </span>
                  )}
                </div>

                <h2 className="text-2xl md:text-3xl font-bold text-[#1B3D2A]">
                  {product.name}
                </h2>

                {/* 標籤 */}
                <div className="mt-3 flex flex-wrap gap-2">
                  {product.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 rounded-full bg-[#1B3D2A]/8 text-sm text-[#1B3D2A] font-medium border border-[#1B3D2A]/10"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* 價格 */}
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="text-sm text-[#9A8A7A]">NT$</span>
                  <span className="text-3xl md:text-4xl font-bold text-[#1B3D2A]">
                    {product.price.toLocaleString()}
                  </span>
                </div>

                <p className="mt-4 text-[#5A6B5A] text-base leading-relaxed">
                  {product.description}
                </p>

                {/* 核心規格快速條列 */}
                <div className="mt-6 bg-[#FAF8F5] rounded-xl border border-[#E8E0D6] p-5 md:p-6">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-shield-check-line text-[#1B3D2A] text-sm" />
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-[#9A8A7A]">保固期</div>
                        <div className="text-sm font-semibold text-[#1B3D2A]">2 年原廠保固</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-calendar-check-line text-[#1B3D2A] text-sm" />
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-[#9A8A7A]">試戴服務</div>
                        <div className="text-sm font-semibold text-[#1B3D2A]">免費 30 天</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-tools-line text-[#1B3D2A] text-sm" />
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-[#9A8A7A]">售後調校</div>
                        <div className="text-sm font-semibold text-[#1B3D2A]">終生免費</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-truck-line text-[#1B3D2A] text-sm" />
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-[#9A8A7A]">配送方式</div>
                        <div className="text-sm font-semibold text-[#1B3D2A]">門市自取 / 宅配</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTA 按鈕 */}
                <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4">
                  <button
                    type="button"
                    onClick={() => navigate('/booking')}
                    className="bg-[#1B3D2A] text-white px-6 md:px-8 py-3.5 rounded-xl text-base font-bold hover:bg-[#143026] transition-colors whitespace-nowrap flex items-center justify-center gap-2 min-h-[52px]"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-calendar-check-line" />
                    </div>
                    預約試戴
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/contact')}
                    className="border border-[#E8E0D6] text-[#5A6B5A] px-6 md:px-8 py-3.5 rounded-xl text-base font-semibold hover:border-[#1B3D2A] hover:text-[#1B3D2A] transition-colors whitespace-nowrap flex items-center justify-center gap-2 min-h-[52px]"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-customer-service-2-line" />
                    </div>
                    聯絡客服諮詢
                  </button>
                </div>
              </SectionReveal>
            </div>
          </div>
        </div>

        {/* 功能特色區 */}
        <div className="px-6 md:px-10 lg:px-16 pb-8 md:pb-12 max-w-7xl mx-auto">
          <ProductFeatures tags={product.tags} />
        </div>

        {/* 規格區 */}
        <div className="px-6 md:px-10 lg:px-16 pb-8 md:pb-12 max-w-7xl mx-auto">
          <ProductSpecs category={product.category} price={product.price} tags={product.tags} />
        </div>

        {/* 相關產品 */}
        <div className="px-6 md:px-10 lg:px-16 pb-8 md:pb-12 max-w-7xl mx-auto">
          <RelatedProducts currentProductId={product.id} category={product.category} currentPrice={product.price} />
        </div>

        {/* 底部 CTA */}
        <div className="px-6 md:px-10 lg:px-16 pb-12 md:pb-20 max-w-7xl mx-auto">
          <SectionReveal delay={2}>
            <div className="bg-[#FAF8F5] rounded-2xl border border-[#E8E0D6] p-6 md:p-10 flex flex-col md:flex-row items-stretch md:items-center gap-5 md:gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-customer-service-2-line text-[#1B3D2A]" />
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-[#D4A574] tracking-wider uppercase">專業建議</span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">
                  不確定這款是否適合您？
                </h3>
                <p className="mt-3 text-sm text-[#5A6B5A] leading-relaxed">
                  每個人的聽力狀況不同，建議先進行免費聽力檢測，由專業聽力師為您推薦最適合的機型。
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/booking')}
                className="shrink-0 bg-[#1B3D2A] text-white px-6 md:px-8 py-3 rounded-xl text-sm font-bold hover:bg-[#143026] transition-colors whitespace-nowrap flex items-center gap-2 min-h-[52px]"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-calendar-check-line" />
                </div>
                免費預約檢測
              </button>
            </div>
          </SectionReveal>
        </div>
      </main>

      <Footer />
    </div>
  );
}